import {chatReducer, type ChatTurnCancelledAction, type ToolCallResult} from '@microsoft/agent-host-protocol'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import type {RelayLease} from '@github-ui/ahp-relay/relay-registry'
import {
  EstablishedAhpSessionResumptionTimeoutError,
  type DispatchReceipt,
  type EstablishedAhpDirectTurnTelemetry,
  type EstablishedAhpResumptionAttempt,
  type EstablishedAhpSessionError,
  type EstablishedAhpPendingMessage,
  type EstablishedAhpSessionCommands,
  type EstablishedAhpSessionIdentity,
  type EstablishedAhpSessionInputController,
  type EstablishedAhpSessionView,
  type EstablishedAhpSessionViewSnapshot,
  type InputCompletion,
  type PreparedPrompt,
  type StopOutcome,
  type ToolConfirmationResponse,
} from './contracts'

function attachmentsFor(prompt: PreparedPrompt) {
  return prompt.attachments && prompt.attachments.length > 0 ? [...prompt.attachments] : undefined
}

const ACTION_TURN_CANCELLED = 'chat/turnCancelled' as ChatTurnCancelledAction['type']

/**
 * React-free command and view client for one identity-bound relay lease.
 *
 * The client intentionally does not own the lease lifecycle. Its containing
 * established-session runtime owns acquisition, reset, and disposal.
 */
export class AhpSessionClient {
  readonly view: EstablishedAhpSessionView
  readonly input: EstablishedAhpSessionInputController
  readonly commands: EstablishedAhpSessionCommands
  readonly identity: EstablishedAhpSessionIdentity
  private readonly lease: RelayLease
  private readonly directTurnTelemetry: EstablishedAhpDirectTurnTelemetry | undefined
  readonly #onTerminalResumptionTimeout: (error: EstablishedAhpSessionError) => void
  readonly #isResumptionRecoveryActive: () => boolean
  readonly #resumptionAttemptForMutation: (start: boolean) => EstablishedAhpResumptionAttempt | undefined

  #clientSeq = 0
  #disposed = false
  #subscriptionsReleased = false
  #terminalError: EstablishedAhpSessionError | undefined
  #viewSnapshot!: EstablishedAhpSessionViewSnapshot
  #optimisticallyCancelledTurnId: string | undefined
  readonly #viewListeners = new Set<() => void>()
  readonly #disposeListeners = new Set<(error: Error) => void>()
  readonly #unsubscribeView: () => void
  readonly #unsubscribeActions: () => void

  constructor(
    identity: EstablishedAhpSessionIdentity,
    lease: RelayLease,
    directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry,
    onTerminalResumptionTimeout: (error: EstablishedAhpSessionError) => void = () => {},
    isResumptionRecoveryActive: () => boolean = () => true,
    resumptionAttemptForMutation: (start: boolean) => EstablishedAhpResumptionAttempt | undefined = () => undefined,
  ) {
    this.identity = identity
    this.lease = lease
    this.directTurnTelemetry = directTurnTelemetry
    this.#onTerminalResumptionTimeout = onTerminalResumptionTimeout
    this.#isResumptionRecoveryActive = isResumptionRecoveryActive
    this.#resumptionAttemptForMutation = resumptionAttemptForMutation
    this.#viewSnapshot = this.readViewSnapshot()
    this.#updateTelemetryReadiness()
    this.#unsubscribeView = this.lease.subscribe(() => {
      if (this.#disposed || !this.lease.isCurrent()) return
      this.#viewSnapshot = this.readViewSnapshot()
      this.#updateTelemetryReadiness()
      for (const listener of this.#viewListeners) listener()
    })
    this.#unsubscribeActions = directTurnTelemetry
      ? this.lease.subscribeActions((envelope, previousState, state) =>
          directTurnTelemetry.acceptedAction(envelope, previousState, state),
        )
      : () => {}
    this.view = {
      getSnapshot: () => this.#viewSnapshot,
      subscribe: listener => {
        this.#viewListeners.add(listener)
        return () => this.#viewListeners.delete(listener)
      },
      observeChat: (channel, observer) => this.lease.observeChat(channel, observer),
      observeChangeset: (channel, observer) => this.lease.observeChangeset(channel, observer),
      subscribeTelemetry: observer => this.lease.subscribeTelemetry(observer),
    }

    this.input = {
      submit: (prompt, requestId, beforeDispatch) => this.submit(prompt, requestId, beforeDispatch),
      queue: (prompt, beforeDispatch) => this.queue(prompt, beforeDispatch),
      steer: (prompt, steeringId, beforeDispatch) => this.steer(prompt, steeringId, beforeDispatch),
      sendNow: message => this.sendNow(message),
      removeQueued: id => this.removeQueued(id),
    }

    this.commands = {
      cancel: turnId => this.cancel(turnId),
      claimActiveClient: tools => this.claimActiveClient(tools),
      completions: (text, offset) => this.lease.completions(text, offset),
      respondToPrompt: response => this.respondToPrompt(response),
      confirmTool: confirmation => this.confirmTool(confirmation),
      completeTool: input => this.completeTool(input),
      renameSession: title => this.renameSession(title),
      setApprovalMode: mode => this.setApprovalMode(mode),
      setApproveAll: enabled => this.setApproveAll(enabled),
      setMode: mode => this.setMode(mode),
    }
  }

  private readViewSnapshot(): EstablishedAhpSessionViewSnapshot {
    const state = this.lease.getSnapshot()
    if (this.#optimisticallyCancelledTurnId && state?.activeTurn?.id !== this.#optimisticallyCancelledTurnId) {
      this.#optimisticallyCancelledTurnId = undefined
    }
    const optimisticState =
      state && this.#optimisticallyCancelledTurnId
        ? chatReducer(state, {
            type: ACTION_TURN_CANCELLED,
            turnId: this.#optimisticallyCancelledTurnId,
            duration: 0,
          })
        : state
    return {
      state: optimisticState,
      status: this.lease.getStatus(),
      reachable: this.lease.getReachable(),
      error: this.lease.getError(),
      failure: this.lease.getFailure?.(),
      modelFailures: this.lease.getModelFailures(),
      provisioning: this.lease.getProvisioning(),
      sessionState: this.lease.getSessionState(),
      passive: this.lease.getPassive(),
      passiveKnown: this.lease.getPassiveKnown(),
      steeringMessages: this.lease.getSteeringMessages(),
    }
  }

  #updateTelemetryReadiness(): void {
    this.directTurnTelemetry?.readinessChanged(
      this.lease.getStatus() === 'connected' &&
        this.lease.getReachable() &&
        this.lease.getPassiveKnown() &&
        !this.lease.getPassive(),
    )
  }

  #releaseSubscriptions(): void {
    if (this.#subscriptionsReleased) return
    this.#subscriptionsReleased = true
    this.#unsubscribeView()
    this.#unsubscribeActions()
  }

  markDisposed(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.directTurnTelemetry?.readinessChanged(false)
    const error = new Error('established AHP session client is disposed')
    for (const listener of this.#disposeListeners) listener(error)
    this.#disposeListeners.clear()
    this.#releaseSubscriptions()
    this.#viewListeners.clear()
  }

  markResumptionTimedOut(error: EstablishedAhpSessionError): void {
    if (this.#disposed || this.#terminalError) return
    this.#terminalError = error
    this.#releaseSubscriptions()
    this.#viewSnapshot = {
      ...this.#viewSnapshot,
      status: 'error',
      reachable: false,
      error: error.message,
      failure: error.failure,
    }
    this.directTurnTelemetry?.readinessChanged(false)
    for (const listener of this.#viewListeners) listener()
  }

  async waitForAccepted(receipt: DispatchReceipt, timeoutMs = 30_000): Promise<void> {
    this.ensureActive()
    if (!receipt.turnId) return
    const isAccepted = () => {
      const state = this.lease.getSnapshot()
      return state?.activeTurn?.id === receipt.turnId || state?.turns?.some(turn => turn.id === receipt.turnId) === true
    }
    if (isAccepted()) return

    await new Promise<void>((resolve, reject) => {
      let settled = false
      let unsubscribe = () => {}
      const finish = (callback: () => void) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        unsubscribe()
        this.#disposeListeners.delete(onDisposed)
        callback()
      }
      const onDisposed = (error: Error) => finish(() => reject(error))
      const timer = setTimeout(
        () =>
          finish(() =>
            reject(new Error('The first AHP turn was not accepted before the acknowledgement window closed.')),
          ),
        timeoutMs,
      )
      this.#disposeListeners.add(onDisposed)
      unsubscribe = this.lease.subscribe(() => {
        if (isAccepted()) finish(resolve)
      })
      if (this.#disposed) onDisposed(new Error('established AHP session client is disposed'))
    })
  }

  private ensureActive(): void {
    if (this.#terminalError) throw this.#terminalError
    if (this.#disposed) throw new Error('established AHP session client is disposed')
  }

  private nextReceipt(
    details: Omit<DispatchReceipt, 'requestId' | 'clientSeq'>,
    requestId: string = crypto.randomUUID(),
  ): DispatchReceipt {
    return {
      requestId,
      clientSeq: ++this.#clientSeq,
      ...details,
    }
  }

  private async ensureMutationLane(requestId?: string): Promise<void> {
    this.ensureActive()
    if (!this.#isResumptionRecoveryActive()) {
      const reconnected = await this.lease.ensureLive(
        requestId ? () => this.directTurnTelemetry?.readinessRequired(requestId) : undefined,
      )
      if (reconnected) await this.lease.whenConnected()
      if (this.lease.getPassive()) throw new Error('relay connection is read-only')
      if (requestId) this.directTurnTelemetry?.readinessObserved(requestId)
      return
    }

    try {
      let deadline: EstablishedAhpResumptionAttempt | undefined
      let announceDeadline: (attempt: EstablishedAhpResumptionAttempt) => void = () => {}
      const deadlineStarted = new Promise<EstablishedAhpResumptionAttempt>(resolve => {
        announceDeadline = resolve
      })
      const reconnecting = this.lease.ensureLive(() => {
        if (requestId) this.directTurnTelemetry?.readinessRequired(requestId)
        deadline = this.#resumptionAttemptForMutation(true)
        if (deadline) announceDeadline(deadline)
      })
      deadline ??= this.#resumptionAttemptForMutation(false)
      let reconnected: boolean
      if (deadline) {
        reconnected = await deadline.waitFor(reconnecting)
      } else {
        const reconnectResult = async () => ({kind: 'reconnected' as const, result: await reconnecting})
        const announcedDeadline = async () => ({kind: 'deadline' as const, attempt: await deadlineStarted})
        const first = await Promise.race([reconnectResult(), announcedDeadline()])
        if (first.kind === 'deadline') {
          deadline = first.attempt
          reconnected = await deadline.waitFor(reconnecting)
        } else {
          reconnected = first.result
        }
      }
      if (reconnected) {
        deadline ??= this.#resumptionAttemptForMutation(false)
        if (deadline) await deadline.waitFor(this.lease.whenConnected(undefined, undefined, deadline.signal))
        else await this.lease.whenConnected()
      }
    } catch (error) {
      if (error instanceof EstablishedAhpSessionResumptionTimeoutError) {
        this.#onTerminalResumptionTimeout(error)
      }
      throw error
    }
    if (this.lease.getPassive()) throw new Error('relay connection is read-only')
    if (requestId) this.directTurnTelemetry?.readinessObserved(requestId)
  }

  private async submit(
    prompt: PreparedPrompt,
    requestId?: string,
    beforeDispatch?: () => Promise<void>,
  ): Promise<DispatchReceipt> {
    const submissionRequestId = requestId ?? crypto.randomUUID()
    this.directTurnTelemetry?.beginSubmission({
      requestId: submissionRequestId,
      turnKind: 'existing_chat',
      startPath: 'active',
      ...(prompt.model?.id ? {modelId: prompt.model.id} : {}),
    })
    try {
      await this.ensureMutationLane(submissionRequestId)
      if (beforeDispatch) await beforeDispatch()
      this.directTurnTelemetry?.turnDispatching(submissionRequestId)
      const turnId =
        prompt.contributedInstructions === undefined
          ? this.lease.dispatchTurn(prompt.text, attachmentsFor(prompt), prompt.model, prompt.agent)
          : this.lease.dispatchTurn(
              prompt.text,
              attachmentsFor(prompt),
              prompt.model,
              prompt.agent,
              prompt.contributedInstructions,
            )
      this.directTurnTelemetry?.turnDispatched(submissionRequestId, turnId)
      return this.nextReceipt({turnId}, submissionRequestId)
    } catch (error) {
      this.directTurnTelemetry?.preDispatchError(submissionRequestId)
      throw error
    }
  }

  private async queue(prompt: PreparedPrompt, beforeDispatch?: () => Promise<void>): Promise<DispatchReceipt> {
    await this.ensureMutationLane()
    if (beforeDispatch) await beforeDispatch()
    const pendingMessageId =
      prompt.contributedInstructions === undefined
        ? this.lease.queueMessage(prompt.text, attachmentsFor(prompt), prompt.model, prompt.agent)
        : this.lease.queueMessage(
            prompt.text,
            attachmentsFor(prompt),
            prompt.model,
            prompt.agent,
            prompt.contributedInstructions,
          )
    return Promise.resolve(this.nextReceipt({pendingMessageId}))
  }

  private async steer(
    prompt: PreparedPrompt,
    steeringId?: string,
    beforeDispatch?: () => Promise<void>,
  ): Promise<DispatchReceipt> {
    await this.ensureMutationLane()
    if (beforeDispatch) await beforeDispatch()
    const pendingMessageId =
      prompt.contributedInstructions === undefined
        ? this.lease.steerMessage(prompt.text, attachmentsFor(prompt), prompt.model, steeringId, prompt.agent)
        : this.lease.steerMessage(
            prompt.text,
            attachmentsFor(prompt),
            prompt.model,
            steeringId,
            prompt.agent,
            prompt.contributedInstructions,
          )
    return Promise.resolve(this.nextReceipt({pendingMessageId}))
  }

  private async sendNow(message: EstablishedAhpPendingMessage): Promise<DispatchReceipt> {
    const receipt = await this.steer(message.prompt, message.id)
    await this.removeQueued(message.id)
    return receipt
  }

  private async removeQueued(id: string): Promise<void> {
    await this.ensureMutationLane()
    this.lease.removeQueuedMessage(id)
    return Promise.resolve()
  }

  private cancel(turnId?: string): StopOutcome {
    this.ensureActive()
    const activeTurnId = turnId ?? this.lease.getSnapshot()?.activeTurn?.id
    const accepted = this.lease.cancelTurn(activeTurnId)
    if (accepted && activeTurnId) {
      this.#optimisticallyCancelledTurnId = activeTurnId
      this.#viewSnapshot = this.readViewSnapshot()
      for (const listener of this.#viewListeners) listener()
    }

    return {
      accepted,
      ...(activeTurnId ? {turnId: activeTurnId} : {}),
    }
  }

  private claimActiveClient(tools: Parameters<RelayLease['claimActiveClient']>[0]): Promise<void> {
    this.ensureActive()
    return this.lease.claimActiveClient(tools)
  }

  private respondToPrompt(input: InputCompletion): void {
    this.ensureActive()
    this.lease.completeInput({
      requestId: input.requestId,
      response: input.response,
      ...(input.answers ? {answers: input.answers} : {}),
    })
  }

  private confirmTool(confirmation: ToolConfirmationResponse): void {
    this.ensureActive()
    this.lease.confirmToolCall(confirmation)
  }

  private completeTool(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void {
    this.ensureActive()
    this.lease.completeToolCall(input)
  }

  private renameSession(title: string): void {
    this.ensureActive()
    this.lease.renameSession(title)
  }

  private async setApproveAll(enabled: boolean): Promise<void> {
    await this.setApprovalMode(enabled ? 'allow-all' : 'manual')
  }

  private async setApprovalMode(mode: SessionApprovalMode): Promise<void> {
    this.ensureActive()
    await this.ensureMutationLane()
    await this.lease.setApprovalMode(mode)
  }

  private setMode(mode: string): Promise<void> {
    this.ensureActive()
    return this.lease.setMode(mode)
  }
}
